import { useEffect, useId, useState } from "react";
import { NavLink, useMatch, useLocation } from "react-router-dom";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import chargeElectricityIcon from "../assets/img/icons/charge-electricity.svg";
import chargeElectricityOutlineIcon from "../assets/img/icons/charge-electricity-outline.svg";
import chargeWaterIcon from "../assets/img/icons/charge-water.svg";
import chargeWaterOutlineIcon from "../assets/img/icons/charge-water-outline.svg";
import loadingArrowIcon from "../assets/img/icons/loading-arrow.svg";
import loadingArrowWhiteIcon from "../assets/img/icons/loading-arrow-white.svg";
import { useBoaterStorage } from "../hooks/useBoaterStorage";
import chargeTypeWhite from "../assets/img/icons/charge-type-white.svg";
import chargeTypeWhiteSolid from "../assets/img/icons/charge-type-white-solid.svg";
import { useTransitionState } from "react-transition-state";
import { withToast } from "../App";
import { useApi } from "../hooks/useApi";

const TransactionLinks = (props) => {
    const { t } = useTranslation();
    const { boaterStorage, updateBoaterStorage } = useBoaterStorage();
    const { transactions } = boaterStorage;
    const activeRoute = useMatch("/boater/transaction/:energy/:transaction_id");
    const [open, setOpen] = useState(
        !!activeRoute || (transactions && transactions.length > 0),
    );
    const activeTransactionId = activeRoute?.params?.transaction_id ?? null;
    const id = useId();
    const { request } = useApi(props);
    const location = useLocation();

    useEffect(() => {
        const fetchTransactions = async () => {
            const queryParams = new URLSearchParams(location.search);
            const email = queryParams.get("email");
            const token = queryParams.get("token");

            let apiQueryString = "";
            if (email) apiQueryString += `email=${encodeURIComponent(email)}`;
            if (token) {
                if (apiQueryString) apiQueryString += "&";
                apiQueryString += `token=${encodeURIComponent(token)}`;
            }

            const endpoint = apiQueryString
                ? `/transactions/?${apiQueryString}`
                : "/transactions/";

            try {
                const data = await request(endpoint, "GET");
                if (data && data.status === "ok" && data.transactions) {
                    updateBoaterStorage({
                        transactions: data.transactions || [],
                    });
                }
            } catch {
                console.log("Error fetching transactions");
            }
        };

        fetchTransactions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const links = (() => {
        const activeTransactionLinks =
            transactions
                ?.filter((transaction) => transaction.fluidType !== undefined)
                .map((transaction) => ({
                    id: transaction.id,
                    to: `/boater/transaction/${transaction.fluidType}/${transaction.id}`,
                    title:
                        transaction.fluidType === "Electricity"
                            ? t("electric_consumption_state")
                            : t("water_consumption_state"),
                    fluid_type: transaction.fluidType,
                })) || [];

        if (activeRoute) {
            const { energy, transaction_id } = activeRoute.params;

            const transactionExists = activeTransactionLinks.some(
                (link) => link.id.toString() === transaction_id.toString(),
            );

            if (!transactionExists) {
                const urlBasedTransaction = {
                    id: transaction_id,
                    to: `/boater/transaction/${energy}/${transaction_id}`,
                    fluid_type: energy,
                };

                return [...activeTransactionLinks, urlBasedTransaction];
            }
        }

        return activeTransactionLinks;
    })();

    const hasActiveOrViewingTransaction = links.length > 0;

    const isLinkActive = (to) => {
        if (!activeRoute) return false;

        const pathParts = to.split("/");
        const linkTransactionId = pathParts[pathParts.length - 1];

        return linkTransactionId === activeTransactionId;
    };

    const iconSrc = activeRoute
        ? open
            ? chargeTypeWhite
            : chargeTypeWhiteSolid
        : chargeTypeWhiteSolid;

    // Transition state for submenu
    const [{ status, isMounted }, toggle] = useTransitionState({
        timeout: 200,
        mountOnEnter: true,
        unmountOnExit: true,
        preEnter: true,
    });

    useEffect(() => {
        toggle(open && hasActiveOrViewingTransaction);
    }, [open, hasActiveOrViewingTransaction, toggle]);

    const mapStatusClasses = (s) => {
        if (!s) return "";
        const classes = [s];
        if (s === "preEnter") classes.push("preEnter");
        if (s === "entering") classes.push("enter");
        if (s === "entered") classes.push("enter-done");
        if (s === "preExit" || s === "exiting") classes.push("exit");
        if (s === "exited") classes.push("exit-done");
        return classes.join(" ").trim();
    };

    return (
        <>
            <li className={`consumption-links ${open ? "active" : ""}`}>
                <button
                    id={`${id}_button`}
                    title={
                        hasActiveOrViewingTransaction
                            ? t("boater_navbar_in_progress")
                            : t("boater_navbar_in_progress_none")
                    }
                    onClick={() =>
                        hasActiveOrViewingTransaction &&
                        setOpen((prev) => !prev)
                    }
                    style={
                        hasActiveOrViewingTransaction
                            ? {}
                            : { cursor: "not-allowed" }
                    }
                    className={`consumption-links__parent${
                        hasActiveOrViewingTransaction ? " active" : ""
                    }`}
                    aria-controls={`${id}_menu`}
                >
                    <img className="nav-icon" src={iconSrc} alt="" />
                </button>
                {isMounted && (
                    <ul
                        id={`${id}_menu`}
                        className={`consumption-links__children consumption-links__children--${status} ${mapStatusClasses(
                            status,
                        )}`}
                        aria-labelledby={`${id}_button`}
                    >
                        {links.map(({ to, title, fluid_type }) => {
                            const active = isLinkActive(to);

                            const icon =
                                fluid_type === "Electricity"
                                    ? active
                                        ? chargeElectricityIcon
                                        : chargeElectricityOutlineIcon
                                    : active
                                      ? chargeWaterIcon
                                      : chargeWaterOutlineIcon;

                            const arrowIcon =
                                active && open
                                    ? loadingArrowIcon
                                    : loadingArrowWhiteIcon;

                            return (
                                <li
                                    className={`consumption-links__item ${fluid_type}`}
                                    key={to}
                                >
                                    <NavLink
                                        to={to}
                                        className={classNames({
                                            "consumption-links__link": true,
                                            [`consumption-links__link--${fluid_type}`]: true,
                                            active: active,
                                        })}
                                        title={title}
                                    >
                                        <img
                                            className="consumption-links__icon"
                                            src={icon}
                                            alt=""
                                        />
                                        <img
                                            className="consumption-links__arrow"
                                            src={arrowIcon}
                                            alt=""
                                        />
                                        <span className="sr-only">{title}</span>
                                    </NavLink>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </li>
        </>
    );
};

export default withToast(TransactionLinks);
